import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Tabs, TabList, TabPanel } from 'react-tabs';
import { Button } from '@twigeducation/ts-fe-components';
import { Print } from '@twigeducation/ts-fe-components/components/Icons';
import SafeHTML from '@twigeducation/react-safe-html';
import Collapse from '../Collapse';
import { TranscriptPanel, ButtonContainer, StyledTab, StyledHeaderTag } from './Styled/Transcripts.styled';
import { useTranslation } from '../../hooks/useTranslation';
import { Translation } from '../Translation';

const Transcripts = ({ className, film, transcripts, supportedLanguages, headerTag: HeaderTag = 'h2' }) => {
    const history = useHistory();
    const [selectedTabIndex, setTabIndex] = useState(0);
    const { t } = useTranslation();
    const tabRefs = useRef([]);
    const panelRefs = useRef([]);
    const printButtonRef = useRef(null);
    const [printTabIndex, setPrintTabIndex] = useState(false);
    const [tabPanelTabIndex, setTabPanelTabIndex] = useState(true);

    const changeTab = index => {
        setTabIndex(index);
        setTabPanelTabIndex(true);
        setPrintTabIndex(false);
    };

    const getTranscriptUrl = (id, slug, locale) => {
        const href = history.createHref({ pathname: `/video/${slug}/${btoa(id)}/transcript/${locale}` });
        return `${window.location.origin}${href}`;
    };

    const handlePanelKeyDown = event => {
        const selectedPanel = document.querySelector('.react-tabs__tab-panel--selected');
        if (event.key === 'Tab') {
            if (event.shiftKey && printTabIndex) {
                if (printButtonRef.current) {
                    printButtonRef.current.focus();
                }
                setPrintTabIndex(false);
                setTabPanelTabIndex(true);

                if (selectedPanel) {
                    selectedPanel.tabIndex = 0;
                    selectedPanel.focus();
                }
                event.preventDefault();
            } else if (!event.shiftKey && tabPanelTabIndex) {
                setTabPanelTabIndex(false);
                setPrintTabIndex(true);

                if (printButtonRef.current) {
                    printButtonRef.current.focus();
                }

                if (selectedPanel) {
                    selectedPanel.tabIndex = -1;
                }
                event.preventDefault();
            }
        }
    };

    const handleTabFocus = index => {
        setTabIndex(index);
        setTabPanelTabIndex(true);
        setPrintTabIndex(false);
        const selectedPanel = document.querySelector('.react-tabs__tab-panel--selected');
        if (selectedPanel) {
            selectedPanel.tabIndex = 0;
        }
    };

    if (transcripts.length > 0) {
        return (
            <Collapse className={className} onKeyDown={handlePanelKeyDown}>
                {transcripts.length > 1 ? (
                    <TranscriptPanel
                        header={
                            <StyledHeaderTag as={HeaderTag}>
                                {t('general.transcripts_panel_header', 'Transcript')}
                            </StyledHeaderTag>
                        }
                        headerClass="transcripts-header"
                        showArrow
                    >
                        <Tabs selectedIndex={selectedTabIndex} onSelect={changeTab} data-at="transcript-languages-tabs">
                            <TabList
                                data-at="transcript-languages-tabs-list"
                                onKeyDown={e => {
                                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }
                                }}
                            >
                                {transcripts
                                    .filter(alternateVersion =>
                                        supportedLanguages.some(
                                            supportedLanguage =>
                                                supportedLanguage.code === alternateVersion.languageCode,
                                        ),
                                    )
                                    .map((transcript, index) => (
                                        <StyledTab
                                            key={`${transcript.languageName}-tab`}
                                            data-at="transcript-language-tab"
                                            ref={el => {
                                                tabRefs.current[index] = el;
                                            }}
                                            onFocus={() => handleTabFocus(index)}
                                        >
                                            {transcript.languageName}
                                        </StyledTab>
                                    ))}
                            </TabList>
                            <ButtonContainer>
                                <Button
                                    iconLeft
                                    secondary
                                    target="_blank"
                                    href={getTranscriptUrl(
                                        film.id,
                                        film.slug,
                                        transcripts[selectedTabIndex].languageCode,
                                    )}
                                    onKeyDown={handlePanelKeyDown}
                                    ref={printButtonRef}
                                    tabIndex={printTabIndex ? 0 : -1}
                                >
                                    <Print size={15} aria-hidden />
                                    <Translation
                                        translationKey="general.transcripts_button_print_title"
                                        defaultValue="Print"
                                    />
                                </Button>
                            </ButtonContainer>
                            {transcripts
                                .filter(alternateVersion =>
                                    supportedLanguages.some(
                                        supportedLanguage => supportedLanguage.code === alternateVersion.languageCode,
                                    ),
                                )
                                .map((transcript, index) => (
                                    <TabPanel
                                        key={`${transcript.languageName}-tab`}
                                        ref={el => {
                                            panelRefs.current[index] = el;
                                        }}
                                        onKeyDown={handlePanelKeyDown}
                                        tabIndex={selectedTabIndex === index ? 0 : -1}
                                    >
                                        <SafeHTML>{transcript.transcript}</SafeHTML>
                                    </TabPanel>
                                ))}
                        </Tabs>
                    </TranscriptPanel>
                ) : (
                    <TranscriptPanel
                        header={
                            <StyledHeaderTag as={HeaderTag}>
                                {t('general.transcripts_panel_header', 'Transcript')}
                            </StyledHeaderTag>
                        }
                        headerClass="transcripts-header"
                    >
                        <ButtonContainer>
                            <Button
                                iconLeft
                                secondary
                                target="_blank"
                                href={getTranscriptUrl(film.id, film.slug, transcripts[selectedTabIndex].languageCode)}
                            >
                                <Print size={15} aria-hidden />
                                <Translation
                                    translationKey="components.transcripts_button_print_title"
                                    defaultValue="Print"
                                />
                            </Button>
                        </ButtonContainer>
                        <SafeHTML>{transcripts[0].transcript}</SafeHTML>
                    </TranscriptPanel>
                )}
            </Collapse>
        );
    }
    return null;
};

Transcripts.defaultProps = {
    className: '',
    supportedLanguages: [{ code: 'en-US' }, { code: 'es-US' }],
    headerTag: 'h2',
};

Transcripts.propTypes = {
    className: PropTypes.string,
    film: PropTypes.shape({
        id: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired,
    }).isRequired,
    transcripts: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            transcript: PropTypes.string.isRequired,
            languageCode: PropTypes.string.isRequired,
            languageName: PropTypes.string.isRequired,
        }).isRequired,
    ).isRequired,
    supportedLanguages: PropTypes.arrayOf(
        PropTypes.shape({
            code: PropTypes.string.isRequired,
        }),
    ),
    headerTag: PropTypes.elementType,
};
export default Transcripts;
